import { memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';
import { type Clip, ClipStatus } from '@air/api/types';
import { ClipUtils, clearThumbnailCache } from '@air/utils-clips';

import { RiveThumbnail } from '~/components/RiveThumbnail';
import { ClipPreview } from '~/components/Shared/ClipList/ClipItem/ClipPreview';
import { GalleryAssetThumbnailImage } from '~/components/v2/components/shared/gallery/GalleryAssetThumbnailImage';
import { CDN_IMAGE_LOAD_TIMEOUT_MS } from '~/constants/assetCard';
import { getImagePreviewFallbackUrl } from '~/constants/cloudfront';
import { getPreferenceClassName, getPreferenceImgixFit, useThumbnailPreference } from '~/hooks/useThumbnailPreference';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import { getCanvasImportPreview, releaseCanvasImportPreview } from '~/utils/canvasImportPreviewCache';
import { cacheDragPreviewSrc } from '~/utils/dragPreviewImageCache';
import { getTransparentPreviewSource, preferredPreviewSrc } from '~/utils/getTransparentPreviewSource';
import { isRiveFile } from '~/utils/RiveUtils';
import { releaseUploadDimensions } from '~/utils/uploadDimensionsCache';
import { getUploadPreview, releaseUploadPreview } from '~/utils/uploadPreviewCache';

export type GalleryAssetThumbnailProps = {
  clip: Clip | PublicClip;
  isHovering: boolean;
  width: number;
  height: number;
  /** Called when the canvas import preview can be dismissed (CDN image loaded or clip failed) */
  onPreviewReleased?: () => void;
};

export const GalleryAssetThumbnail = memo(
  ({ clip, isHovering, width, height, onPreviewReleased }: GalleryAssetThumbnailProps) => {
    const videoSrc = clip.assets.previewVideo;
    const playbackRate = ClipUtils.determinePlaybackRate(clip.duration ?? 0, videoSrc ?? '');
    const vertical = clip.status === ClipStatus.transcoded && !ClipUtils.isLandscape(clip.rotation);
    const { getPreference } = useThumbnailPreference();
    const boardId = useSelector(CentralizedBoardSelectors.boardId);
    const isRive = isRiveFile(clip);

    const thumbnailPreference = useMemo(() => getPreference(boardId), [boardId, getPreference]);

    // During processing, prefer the local preview (canvas import or upload) for instant display.
    // Fall back to clip.assets.image so videos, PDFs, and PPTs can show their backend-generated
    // thumbnail while still transcoding.
    const transparentPreview = useMemo(
      () =>
        getTransparentPreviewSource(clip.ext, {
          image: clip.assets.image,
          original: clip.assets.original,
        }),
      [clip.assets.image, clip.assets.original, clip.ext],
    );
    const isWaitingForProcessedPreview = ClipUtils.isProcessing(clip.status) && !clip.assets.spriteSheet?.src;
    const localPreviewSrc =
      getCanvasImportPreview(clip.importedName) ?? getUploadPreview(clip.importedName) ?? clip.assets.image;
    const transcodedSrc = preferredPreviewSrc(transparentPreview, clip.assets.image);
    const imageSrc = isWaitingForProcessedPreview ? localPreviewSrc : transcodedSrc;

    useEffect(() => {
      if (!clip.importedName || ClipUtils.isProcessing(clip.status)) {
        return;
      }

      releaseCanvasImportPreview(clip.importedName);
      releaseUploadPreview(clip.importedName);
      releaseUploadDimensions(clip.importedName);

      // For failed/nonTranscodable: no image will load, clear immediately
      // For transcoded: onPreviewReleased fires via onFinalImageLoad when CDN image loads,
      // but fall back after 5s in case the CDN image never loads (network error, CDN 404)
      if (ClipUtils.isFailed(clip.status) || ClipUtils.isNonTranscodable(clip.status)) {
        onPreviewReleased?.();
      } else {
        const timeout = setTimeout(() => onPreviewReleased?.(), CDN_IMAGE_LOAD_TIMEOUT_MS);

        return () => clearTimeout(timeout);
      }
    }, [clip.importedName, clip.status, onPreviewReleased]);

    const handleSrcLoaded = useCallback(
      (currentSrc: string) => {
        cacheDragPreviewSrc(clip.id, currentSrc);
      },
      [clip.id],
    );

    const CardThumbnail = useMemo(() => {
      const isProcessing = ClipUtils.isProcessing(clip.status);

      // If the Rive file is incompatible, RiveThumbnail will show its fallback
      if (isRive && !isProcessing && clip.assets.original) {
        return <RiveThumbnail src={clip.assets.original} width={width} height={height} borderRadius={12} />;
      }

      return (
        <GalleryAssetThumbnailImage
          {...clip}
          className={getPreferenceClassName(thumbnailPreference)}
          animatedSrc={clip.type === 'animated' && isHovering && videoSrc ? videoSrc : ''}
          fallbackUrl={
            transparentPreview.preserveTransparency && transparentPreview.fallbackSrc
              ? getImagePreviewFallbackUrl(transparentPreview.fallbackSrc) || undefined
              : undefined
          }
          imgixParams={{
            fit: getPreferenceImgixFit(thumbnailPreference),
          }}
          onFinalImageLoad={onPreviewReleased}
          preserveTransparency={transparentPreview.preserveTransparency && imageSrc === transparentPreview.src}
          processedPreviewSrc={
            transparentPreview.preserveTransparency && imageSrc === transparentPreview.src
              ? transparentPreview.fallbackSrc
              : undefined
          }
          onSrcLoaded={handleSrcLoaded}
          src={clearThumbnailCache({
            clipExt: clip.ext,
            clipStatus: clip.status,
            imageSrc,
            clipUpdatedAt: clip.updatedAt,
          })}
          imageHeight={clip.height}
          imageWidth={clip.width}
          width={width}
          height={height}
        />
      );
    }, [
      clip,
      imageSrc,
      isHovering,
      videoSrc,
      thumbnailPreference,
      width,
      height,
      isRive,
      onPreviewReleased,
      handleSrcLoaded,
      transparentPreview,
    ]);

    return (
      <>
        {CardThumbnail}
        {!!videoSrc && clip.type === 'video' && !ClipUtils.isProcessing(clip.status) && isHovering && (
          <Suspense fallback={CardThumbnail}>
            <ClipPreview vertical={vertical} playbackRate={playbackRate} status={clip.status} src={videoSrc} />
          </Suspense>
        )}
      </>
    );
  },
);

GalleryAssetThumbnail.displayName = 'GalleryAssetThumbnail';
